import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { QRCodeSVG } from 'qrcode.react';
import mabarService from '@/services/mabarService';
import { useSystemBarsStyle, systemBarsIconsForBackground } from '@/hooks/useSystemBarsStyle';
import showToast from '@/components/ui/Toast';
import { downloadOrShareBlob } from '@/utils/downloadBlob';
import { getLocalTimezoneName } from '@/utils/dateUtils';

export interface PaymentCheckoutPayload {
  version?: string;
  ui_flow?: string;
  status?: string;
  fallback_invoice_url?: string;
  expires_at?: string;
  qr_string?: string;
  qris_string?: string;
  instructions?: {
    present?: Array<{
      kind: string;
      value?: string;
      raw_value?: string;
      label?: string;
    }>;
    redirect?: {
      deeplink_url?: string;
      web_url?: string;
    };
  };
}

export const MabarCheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderNumber: paramOrderNumber } = useParams<{ orderNumber?: string }>();
  useSystemBarsStyle(systemBarsIconsForBackground('light'));

  const [orderCode, setOrderCode] = useState<string>(
    paramOrderNumber || location.state?.orderNumber || location.state?.invoiceNumber || ''
  );
  const [eventTitle, setEventTitle] = useState<string>(
    location.state?.eventName || location.state?.event_name || 'Mabar Event'
  );
  const [amount, setAmount] = useState<number>(location.state?.amount || 0);
  const [payDeadline, setPayDeadline] = useState<string>(
    location.state?.deadline || `hari ini 23.59 ${getLocalTimezoneName()}`
  );

  const returnUrl = location.state?.returnUrl || location.state?.redirectUrl || '/';

  const [paymentInstruction, setPaymentInstruction] = useState<PaymentCheckoutPayload | null>(
    location.state?.paymentInstruction || null
  );
  const [isLoading, setIsLoading] = useState<boolean>(!paymentInstruction);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  // Fetch checkout payload & order details from backend using orderCode from URL
  useEffect(() => {
    const currentCode = paramOrderNumber || orderCode;
    if (!currentCode) return;

    const fetchCheckout = async () => {
      setIsLoading(true);
      try {
        const res = await mabarService.getTransactionCheckout(currentCode);
        if (res?.success) {
          if (res.payload) setPaymentInstruction(res.payload);
          if (res.event_title) setEventTitle(res.event_title);
          if (res.amount) setAmount(res.amount);
          if (res.order_number) setOrderCode(res.order_number);
          if (res.payment_deadline) setPayDeadline(res.payment_deadline);
        } else if (res?.invoice_url) {
          setPaymentInstruction({
            version: 'v2',
            ui_flow: 'REDIRECT',
            status: 'PENDING',
            fallback_invoice_url: res.invoice_url,
          });
          if (res.event_title) setEventTitle(res.event_title);
          if (res.amount) setAmount(res.amount);
        }
      } catch (err: any) {
        console.warn('Fetch checkout error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCheckout();
  }, [paramOrderNumber]);

  const activeOrderCode = orderCode || paramOrderNumber || 'ORDC-54-1785170953';

  // Polling payment status every 3 seconds
  useEffect(() => {
    if (!activeOrderCode) return;

    const interval = setInterval(async () => {
      try {
        const statusRes = await mabarService.getTransactionStatus(activeOrderCode);
        const status = (statusRes?.status || '').toLowerCase();
        if (status === 'paid' || status === 'settled') {
          clearInterval(interval);
          showToast.success('Pembayaran QRIS berhasil dikonfirmasi!');
          setTimeout(() => {
            navigate(returnUrl, { replace: true });
          }, 1500);
        }
      } catch {
        // Silent error on background poll
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [activeOrderCode, navigate, returnUrl]);

  // Copy order number
  const handleCopyOrderNumber = () => {
    if (!activeOrderCode) return;
    navigator.clipboard.writeText(activeOrderCode);
    setCopied(true);
    showToast.success('No. Order berhasil disalin');
    setTimeout(() => setCopied(false), 2000);
  };

  // Check Payment Status Button
  const handleCheckStatus = async () => {
    if (!activeOrderCode) {
      showToast.success('Mengecek status pembayaran...', { haptic: false });
      return;
    }
    setIsRegenerating(true);
    try {
      const statusRes = await mabarService.getTransactionStatus(activeOrderCode);
      const status = (statusRes?.status || 'PENDING').toUpperCase();
      if (status === 'PAID' || status === 'SETTLED') {
        showToast.success('Pembayaran Lunas! Mengalihkan...');
        setTimeout(() => navigate(returnUrl, { replace: true }), 1200);
      } else {
        showToast.success(`Status Pembayaran: ${status}`, { haptic: false });
      }
    } catch {
      showToast.success('Status Pembayaran: PENDING (Menunggu Pembayaran)', { haptic: false });
    } finally {
      setIsRegenerating(false);
    }
  };

  // Handle QRIS Regenerate
  const handleRegenerateQris = async (force: boolean = true) => {
    if (!activeOrderCode) return;
    setIsRegenerating(true);
    try {
      const res = await mabarService.getTransactionCheckout(activeOrderCode, force);
      if (res?.success && res.payload) {
        setPaymentInstruction(res.payload);
        showToast.success('Berhasil membuat QRIS baru!');
      } else {
        showToast.error(res?.message || 'Gagal membuat ulang QRIS');
      }
    } catch (err: any) {
      showToast.error(err?.message || 'Gagal memperbarui QRIS');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Download QRIS PNG
  const handleDownloadQris = async () => {
    const svgElement = document.getElementById('qris-qrcode-svg') as SVGSVGElement | null;
    if (!svgElement) {
      showToast.error('Gambar QRIS tidak ditemukan. Muat ulang halaman lalu coba lagi.');
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const objectUrl = window.URL.createObjectURL(svgBlob);

    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Gagal memuat kode QRIS. Coba lagi.'));
        img.src = objectUrl;
      });

      const canvas = document.createElement('canvas');
      const width = 600;
      const height = 840;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Gagal membuat gambar QRIS. Coba lagi.');
      }

      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);

      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = 4;
      ctx.strokeRect(10, 10, width - 20, height - 20);

      ctx.fillStyle = '#FC480D';
      ctx.fillRect(10, 10, width - 20, 14);

      ctx.font = 'bold 22px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#111827';
      ctx.textAlign = 'center';
      ctx.fillText('SPORTO - PEMBAYARAN MABAR', width / 2, 60);

      ctx.font = '500 13px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#6B7280';
      ctx.fillText('Standar Pembayaran QRIS Nasional', width / 2, 82);

      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(30, 105);
      ctx.lineTo(width - 30, 105);
      ctx.stroke();

      ctx.font = 'bold 12px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#9CA3AF';
      ctx.fillText('NO. ORDER TRANSAKSI', width / 2, 135);

      ctx.font = 'bold 20px monospace';
      ctx.fillStyle = '#111827';
      ctx.fillText(activeOrderCode, width / 2, 165);

      ctx.font = 'bold 12px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#9CA3AF';
      ctx.fillText('EVENT', width / 2, 200);

      ctx.font = 'bold 16px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#FC480D';
      ctx.fillText(eventTitle, width / 2, 225);

      if (amount > 0) {
        ctx.font = 'bold 12px system-ui, -apple-system, sans-serif';
        ctx.fillStyle = '#9CA3AF';
        ctx.fillText('TOTAL PEMBAYARAN', width / 2, 260);

        ctx.font = 'bold 24px system-ui, -apple-system, sans-serif';
        ctx.fillStyle = '#10B981';
        ctx.fillText(formatCurrency(amount), width / 2, 290);
      }

      const qrY = amount > 0 ? 320 : 250;
      const qrSize = 360;
      const qrX = (width - qrSize) / 2;

      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(qrX - 15, qrY - 15, qrSize + 30, qrSize + 30);
      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = 2;
      ctx.strokeRect(qrX - 15, qrY - 15, qrSize + 30, qrSize + 30);

      ctx.drawImage(image, qrX, qrY, qrSize, qrSize);

      const footerY = qrY + qrSize + 40;
      ctx.font = 'bold 12px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#6B7280';
      ctx.fillText('CV RAGA SPORT INDONESIA', width / 2, footerY);

      ctx.font = '500 11px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#9CA3AF';
      ctx.fillText('Simpan gambar ini untuk melakukan pembayaran via e-wallet / M-Banking', width / 2, footerY + 20);

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });
      if (!blob) {
        throw new Error('Gagal membuat gambar QRIS. Coba lagi.');
      }

      const result = await downloadOrShareBlob(blob, `qris-${activeOrderCode}.png`);
      if (result === 'saved') {
        showToast.success('Gambar QRIS berhasil diunduh/disimpan ke Galeri');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Gagal mengunduh gambar QRIS';
      showToast.error(message);
    } finally {
      window.URL.revokeObjectURL(objectUrl);
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    })
      .format(val)
      .replace('IDR', 'Rp.');
  };

  const presentInstructions = paymentInstruction?.instructions?.present || [];
  const redirectInstruction = paymentInstruction?.instructions?.redirect;
  const qrInstruction = presentInstructions.find((item) => item.kind === 'QR_STRING');

  const hasValidQr = Boolean(
    qrInstruction?.value ||
    qrInstruction?.raw_value ||
    paymentInstruction?.qr_string ||
    paymentInstruction?.qris_string
  );

  const statusUpper = (paymentInstruction?.status || '').toUpperCase();
  const isExpired =
    statusUpper === 'EXPIRED' ||
    statusUpper === 'EXPIRE' ||
    statusUpper === 'FAILED' ||
    (paymentInstruction?.expires_at ? new Date() > new Date(paymentInstruction.expires_at) : false);

  const needsRegenerate = !hasValidQr || isExpired || statusUpper === 'SUCCEEDED';

  const [hasAutoRegenerated, setHasAutoRegenerated] = useState(false);

  useEffect(() => {
    if (!isLoading && paymentInstruction && needsRegenerate && !hasAutoRegenerated && !isRegenerating) {
      setHasAutoRegenerated(true);
      handleRegenerateQris(true);
    }
  }, [isLoading, paymentInstruction, needsRegenerate, hasAutoRegenerated, isRegenerating]);

  // Guaranteed QR Value matching sporto.id logic so QR code is NEVER blank
  const qrValue =
    qrInstruction?.value ||
    qrInstruction?.raw_value ||
    paymentInstruction?.qr_string ||
    paymentInstruction?.qris_string ||
    redirectInstruction?.deeplink_url ||
    redirectInstruction?.web_url ||
    paymentInstruction?.fallback_invoice_url ||
    `https://sporto.id/event/checkout/${orderCode}`;

  return (
    <div className=" text-[#0E1726] flex flex-col items-center pb-4 select-none">
      {/* Main Content Area */}
      <div className="w-full max-w-sm p-4 flex flex-col gap-4">
        {/* 1. Header Section Badge & Title */}
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-wider text-[#0BCF46] bg-[#E9F6EE]">
            <Icon icon="ph:qr-code" className="w-3.5 h-3.5" />
            PEMBAYARAN QRIS MABAR
          </span>
          <h2 className="text-xl font-bold text-[#0E1726]">Checkout Pembayaran</h2>
          <p className="text-xs text-[#8C8C8C] max-w-xs leading-relaxed">
            Scan kode QRIS di bawah ini untuk menyelesaikan transaksi
          </p>
        </div>

        {/* 2. Order Details Card */}
        <div className="bg-white rounded-2xl shadow-[inset_0_0_0_1px_#E0E6ED] flex flex-col overflow-hidden">
          {/* Row 1: No. Order */}
          <div className="flex items-center justify-between gap-3 p-3.5">
            <span className="text-xs text-[#8C8C8C]">No. Order</span>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-bold text-[#0E1726] truncate">
                {activeOrderCode}
              </span>
              <button
                type="button"
                onClick={handleCopyOrderNumber}
                className="p-1 rounded text-gray-500 hover:text-[#FC480D] active:scale-95 transition-all cursor-pointer flex-shrink-0"
                title="Salin No. Order"
              >
                <Icon icon={copied ? 'ph:check-bold' : 'ph:copy'} className="w-4 h-4 text-[#8C8C8C]" />
              </button>
            </div>
          </div>

          <div className="h-px bg-[#E0E6ED]" />

          {/* Row 2: Event */}
          <div className="flex items-center justify-between gap-3 p-3.5">
            <span className="text-xs text-[#8C8C8C] flex-shrink-0">Event</span>
            <span className="text-xs font-semibold text-[#0E1726] truncate text-right">
              {eventTitle}
            </span>
          </div>

          <div className="h-px bg-[#E0E6ED]" />

          {/* Row 3: Batas bayar */}
          <div className="flex items-center justify-between gap-3 p-3.5">
            <span className="text-xs text-[#8C8C8C] flex-shrink-0">Batas bayar</span>
            <span className="text-xs font-semibold text-[#E7515A] flex-shrink-0">
              {payDeadline}
            </span>
          </div>

          {/* Row 4: Total bayar */}
          <div className="flex items-center justify-between gap-3 p-3.5 bg-[#F6F6F6] border-t border-[#E0E6ED]">
            <span className="text-xs font-semibold text-[#0E1726]">Total bayar</span>
            <span className="text-lg font-bold text-[#FC480D]">
              {formatCurrency(amount)}
            </span>
          </div>
        </div>

        {/* 3. QRIS Code Display Card */}
        <div className="flex flex-col items-center gap-3">
          <div className="bg-white rounded-2xl shadow-[inset_0_0_0_1px_#E0E6ED] p-4 flex items-center justify-center">
            {isLoading || isRegenerating ? (
              <div className="w-[212px] h-[212px] flex flex-col items-center justify-center gap-2 text-center p-2">
                <Icon icon="ph:spinner" className="w-8 h-8 text-[#FC480D] animate-spin" />
                <span className="text-[11px] font-semibold text-gray-500">
                  {isRegenerating ? 'Membuat QRIS Baru...' : 'Memuat QRIS...'}
                </span>
              </div>
            ) : needsRegenerate ? (
              <div className="w-[212px] min-h-[212px] flex flex-col items-center justify-center gap-2 text-center p-3">
                <Icon icon="ph:warning-circle-bold" className="w-8 h-8 text-amber-500" />
                <span className="text-xs font-bold text-[#0E1726]">QRIS Tidak Aktif</span>
                <span className="text-[11px] text-gray-500 font-medium leading-snug">
                  Kode QRIS belum ada atau sudah kadaluwarsa.
                </span>
                <button
                  type="button"
                  onClick={() => handleRegenerateQris(true)}
                  className="mt-1 px-3 py-1.5 rounded-lg bg-[#FC480D] hover:bg-[#D83B08] text-white text-[11px] font-bold transition-all cursor-pointer shadow-2xs"
                >
                  Buat QRIS Baru
                </button>
              </div>
            ) : (
              <div className="w-[212px] h-[212px] flex items-center justify-center">
                <QRCodeSVG
                  id="qris-qrcode-svg"
                  value={qrValue}
                  size={212}
                  includeMargin={false}
                />
              </div>
            )}
          </div>

          {/* Download Button */}
          <button
            type="button"
            onClick={handleDownloadQris}
            className="px-4 py-2.5 rounded-xl bg-[#FFF4EE] text-[#FC480D] text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-[#FFEAE0] active:scale-98 transition-all cursor-pointer shadow-xs"
          >
            <Icon icon="ph:download-simple" className="w-4 h-4" />
            <span>Download Gambar QRIS</span>
          </button>

          <span className="text-[10.5px] font-semibold tracking-wider text-[#8C8C8C]">
            STANDAR PEMBAYARAN NASIONAL
          </span>
        </div>

        <div className="h-px bg-[#E0E6ED] my-1" />

        {/* 4. Instructions Card */}
        <div className="bg-[#F6F6F6] rounded-xl p-3.5 flex flex-col gap-2.5 text-xs text-[#0E1726]">
          <div className="flex items-center gap-1.5 font-bold text-xs text-[#0E1726]">
            <Icon icon="ph:list-numbers" className="w-4 h-4 text-[#FC480D]" />
            <span>Instruksi Cara Pembayaran QRIS:</span>
          </div>
          <div className="flex items-start gap-2 text-xs leading-relaxed text-[#1F1F1F]">
            <span className="font-bold text-[#8C8C8C] flex-shrink-0">1.</span>
            <span>Buka aplikasi e-wallet (GoPay, OVO, DANA, ShopeePay) atau M-Banking (BCA Mobile, Livin, BRImo, BNI, dll).</span>
          </div>
          <div className="flex items-start gap-2 text-xs leading-relaxed text-[#1F1F1F]">
            <span className="font-bold text-[#8C8C8C] flex-shrink-0">2.</span>
            <span>Pilih menu Scan / Bayar QRIS.</span>
          </div>
          <div className="flex items-start gap-2 text-xs leading-relaxed text-[#1F1F1F]">
            <span className="font-bold text-[#8C8C8C] flex-shrink-0">3.</span>
            <span>Arahkan kamera ke kode QRIS di atas.</span>
          </div>
          <div className="flex items-start gap-2 text-xs leading-relaxed text-[#1F1F1F]">
            <span className="font-bold text-[#8C8C8C] flex-shrink-0">4.</span>
            <span>Periksa nominal & selesaikan pembayaran.</span>
          </div>
        </div>

        {/* 5. Action Buttons */}
        <button
          type="button"
          onClick={handleCheckStatus}
          disabled={isRegenerating}
          className="h-11 rounded-xl bg-[#FC480D] text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#D83B08] active:scale-98 transition-all cursor-pointer shadow-sm disabled:opacity-50"
        >
          <Icon icon="ph:arrows-clockwise" className={`w-4.5 h-4.5 ${isRegenerating ? 'animate-spin' : ''}`} />
          <span>Cek Status Pembayaran</span>
        </button>

        <button
          type="button"
          onClick={() => navigate(returnUrl)}
          className="h-11 rounded-xl bg-white shadow-[inset_0_0_0_1px_#E0E6ED] text-[#0E1726] font-semibold text-sm flex items-center justify-center gap-2 hover:bg-gray-50 active:scale-98 transition-all cursor-pointer"
        >
          <Icon icon="ph:arrow-left" className="w-4 h-4" />
          <span>Kembali ke Event</span>
        </button>
      </div>
    </div>
  );
};

export default MabarCheckoutPage;
